import { Button, Card, ConfigProvider, Form, Input, Tag, Typography } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LoginHand } from '../../store/Auth/LoginSlice'
import useTranslation from '../../localization/useTranslation'
import LanguageToggler from '../../components/LanguageToggler/LanguageToggler'
import ValidationErrorAlert from '../../components/ValidationErrorAlert'
import { applyValidationErrorsToForm } from '../../utilities/handleApiValidationError'

const { Title, Text } = Typography

const backofficeLoginTheme = {
    token: {
        colorPrimary: '#6366f1',
        borderRadius: 8,
    },
}

const LogIn = () => {
    const dispatch = useDispatch()
    const { loading, errorMessages, fieldErrors } = useSelector((state) => state.login)
    const [from] = Form.useForm()
    const { t, language } = useTranslation()

    useEffect(() => {
        if (fieldErrors) {
            applyValidationErrorsToForm(from, fieldErrors)
        }
    }, [fieldErrors, from])

    const onFinish = (values) => {
        dispatch(LoginHand(values))
    }

    return (
        <ConfigProvider theme={backofficeLoginTheme}>
            <div
                style={{
                    minHeight: '100vh',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px 16px',
                    background: 'linear-gradient(145deg, #0f172a 0%, #1e1b4b 45%, #312e81 100%)',
                }}
            >
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
                    <LanguageToggler variant="round" />
                </div>

                <Card
                    style={{
                        width: 440,
                        maxWidth: '100%',
                        boxShadow: '0 24px 48px rgba(15, 23, 42, 0.35)',
                        borderTop: '4px solid #6366f1',
                    }}
                    styles={{ body: { padding: '32px 28px 28px' } }}
                >
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <div
                            style={{
                                width: 56,
                                height: 56,
                                margin: '0 auto 16px',
                                borderRadius: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                color: '#fff',
                                fontSize: 26,
                            }}
                        >
                            <SafetyCertificateOutlined />
                        </div>
                        <Tag color="purple" style={{ marginBottom: 12 }}>
                            {t('auth.login.backofficeTitle')}
                        </Tag>
                        <Title level={3} style={{ marginBottom: 4 }}>
                            Dentrist
                        </Title>
                        <Text type="secondary">{t('auth.login.backofficeSubtitle')}</Text>
                    </div>

                    <ValidationErrorAlert
                        messages={errorMessages}
                        title={t('common.validationError')}
                    />

                    <Form
                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                        form={from}
                        onFinish={onFinish}
                        labelCol={{ span: 24 }}
                        layout="vertical"
                        requiredMark={false}
                    >
                        <Form.Item
                            name="email"
                            label={t('auth.login.email.label')}
                            rules={[
                                {
                                    required: true,
                                    message: t('auth.login.email.required'),
                                },
                                {
                                    type: 'email',
                                    message: t('auth.login.email.invalid'),
                                },
                            ]}
                        >
                            <Input type="email" placeholder={t('auth.login.email.placeholder')} size="large" />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label={t('auth.login.password.label')}
                            rules={[
                                {
                                    required: true,
                                    message: t('auth.login.password.required'),
                                },
                            ]}
                        >
                            <Input.Password placeholder={t('auth.login.password.placeholder')} size="large" />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 12 }}>
                            <Button block type="primary" htmlType="submit" loading={loading} size="large">
                                {t('auth.login.submit')}
                            </Button>
                        </Form.Item>
                    </Form>

                    <Text
                        type="secondary"
                        style={{ display: 'block', textAlign: 'center', fontSize: 12, lineHeight: 1.5 }}
                    >
                        {t('auth.login.backofficeNotice')}
                    </Text>
                </Card>
            </div>
        </ConfigProvider>
    )
}

export default LogIn
